import { ThemeProvider } from '@mui/material'
import { WindowMU } from 'app/types/WindowMU'
import globalMU from 'globalMU'
import React from 'react'
import { createRoot, Root } from 'react-dom/client'
import { LimiteDeErroReact } from './componentes-basicos/LimiteDeErroReact'
import { getComponentWithStore } from './getComponentWithStore'
import { theme } from './materialUiTheme'

declare var window:WindowMU

const idsElementosConteudoPrincipal = [
    "itens-orcamento-react",
    "forma-pagamento-orcamento-region-react",
    "frete-peso-cubagem-region-react",
    "pesquisa-cliente-orcamento-region-react",
    "comissoes-orcamento-region-react",
    "menu-consulta-produtos-react",
    "conteudo-pagina"
]

// React 18: `ReactDOM.render` saiu em favor de `createRoot`. A diferença que obriga este registro
// é que a raiz é um OBJETO com ciclo de vida próprio — chamar `createRoot` duas vezes no mesmo
// elemento é erro ("You are calling createRoot() on a container that has already been passed to
// createRoot()"), e `root.unmount()` a destrói de vez. Como esta ponte é chamada repetidas vezes
// pelos controllers Backbone para o MESMO container (cada troca de aba), a raiz precisa ser
// criada uma vez, reaproveitada em todo render seguinte e removida do mapa ao desmontar — para
// que o próximo render crie uma raiz nova em vez de reusar uma morta.
const raizesPorElemento = new Map<string, Root>()

const obterOuCriarRaiz = (idElemento: string, elemento: HTMLElement): Root => {
    const raizExistente = raizesPorElemento.get(idElemento)
    if (raizExistente) {
        return raizExistente
    }

    const novaRaiz = createRoot(elemento)
    raizesPorElemento.set(idElemento, novaRaiz)
    return novaRaiz
}

const componentesRenderizados = new Set<string>()

const obterIdElementoContainerSemSharp = (idElementoContainer: string) => globalMU.removerSharpDeIdDeElemento(idElementoContainer)

const obterElementoContainer = (idElementoContainer: string) => {
    const idElementoContainerSemSharp = obterIdElementoContainerSemSharp(idElementoContainer)
    return {
        idElementoContainerSemSharp,
        elemento: document.getElementById(idElementoContainerSemSharp)
    }
}

const obterElementoReact = (Component: any, usaStoreRedux: boolean) =>
    <LimiteDeErroReact>
        {usaStoreRedux ? getComponentWithStore(Component) : (
            <ThemeProvider theme={theme}>
                {React.isValidElement(Component) ? Component : <Component />}
            </ThemeProvider>
        )}
    </LimiteDeErroReact>

export function renderizarComponentReact(Component: any, idElementoContainer:string, usaStoreRedux: boolean = false) : boolean{
    
    try {
        const { idElementoContainerSemSharp, elemento } = obterElementoContainer(idElementoContainer)

        if (!elemento) {
            return false
        }

        if (elemento.style.display !== "block") {
            elemento.style.display = "block"
        }

        obterOuCriarRaiz(idElementoContainerSemSharp, elemento).render(
            obterElementoReact(Component, usaStoreRedux)
        )

        componentesRenderizados.add(idElementoContainerSemSharp)
        return true
    } catch (error) { 
        console.warn(`falha ao renderizar componente react no elemento ${idElementoContainer}`)
        console.warn(error)
        return false
    }
    
}

window.renderizarComponentReact = renderizarComponentReact

export function removerComponenteReact(idElementoContainer:string) : boolean {
    try {
        const { idElementoContainerSemSharp, elemento } = obterElementoContainer(idElementoContainer)

        if (!elemento) {
            return false
        }

        if (!componentesRenderizados.has(idElementoContainerSemSharp) && !elemento.hasChildNodes()) {
            return true
        }

        const raiz = raizesPorElemento.get(idElementoContainerSemSharp)
        if (raiz) {
            // Desmonte SÍNCRONO, de propósito. O fluxo da tela é
            // `esconderContainers()` -> remove tudo -> renderiza a aba nova NO MESMO
            // "conteudo-pagina", tudo dentro do mesmo clique. Adiar o unmount (setTimeout) faria
            // o `createRoot` seguinte encontrar o elemento ainda dono de uma raiz viva e, pior,
            // o unmount atrasado apagaria o componente recém-renderizado.
            // É seguro porque quem chama é um handler do Backbone/jQuery, nunca um render do
            // React — a restrição do React vale só para unmount durante o próprio render.
            raiz.unmount()
            raizesPorElemento.delete(idElementoContainerSemSharp)
        }

        componentesRenderizados.delete(idElementoContainerSemSharp)
        return true
    } catch (error) {
        console.warn(`falha ao remover componente react do elemento ${idElementoContainer}`)
        console.warn(error)
        return false
    }
}

window.removerComponenteReact = removerComponenteReact

export function removerTodosComponentesReactDoConteudoPrincipal() {
    for (const idElemento of idsElementosConteudoPrincipal) {
        try {
            removerComponenteReact(idElemento)
        } catch (error) {
            console.warn(`Falha ao remover componente react do elemento ${idElemento}`)
        }
    }
}

window.removerTodosComponentesReactDoConteudoPrincipal = removerTodosComponentesReactDoConteudoPrincipal
